import type { ReactNode, SVGProps } from "react";
import { useI18n } from "../i18n";

// Uniform inline icons (Lucide-style: 24px grid, 1.8 stroke, currentColor) so
// the action rows share one size and weight instead of mismatched glyphs.
function Icon({ children, ...props }: SVGProps<SVGSVGElement>) {
  return (
    <svg
      className="sidebar-action-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      {children}
    </svg>
  );
}

const NewChatIcon = () => (
  <Icon>
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </Icon>
);
const SettingsIcon = () => (
  <Icon>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" />
  </Icon>
);
const ScheduledIcon = () => (
  <Icon>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </Icon>
);
const BillingIcon = () => (
  <Icon>
    <rect x="2" y="5" width="20" height="14" rx="2" />
    <path d="M2 10h20" />
  </Icon>
);
const AdminIcon = () => (
  <Icon>
    <path d="M3 21h18" />
    <path d="M7 17V9" />
    <path d="M12 17V5" />
    <path d="M17 17v-6" />
  </Icon>
);
const SignOutIcon = () => (
  <Icon>
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <path d="m16 17 5-5-5-5" />
    <path d="M21 12H9" />
  </Icon>
);
const CloseIcon = () => (
  <Icon>
    <path d="M18 6 6 18M6 6l12 12" />
  </Icon>
);

interface Props {
  open: boolean;
  isLocal: boolean;
  email?: string | null;
  onClose: () => void;
  onNewChat: () => void;
  onOpenSettings: () => void;
  onOpenCron?: () => void;
  onOpenBilling?: () => void;
  onOpenAdmin?: () => void;
  onSignOut: () => void;
  // The history body: the cloud conversation list, or the local "tasks" entry.
  children: ReactNode;
}

// ChatGPT-style left nav: collapsed by default (only the `-/` mark shows in the
// header); expanding slides this overlay in with the action icons stacked at the
// top and the conversation history beneath. Reuses the drawer overlay mechanics.
export function Sidebar({
  open,
  isLocal,
  email,
  onClose,
  onNewChat,
  onOpenSettings,
  onOpenCron,
  onOpenBilling,
  onOpenAdmin,
  onSignOut,
  children
}: Props) {
  const { t } = useI18n();

  // Run an action and collapse the sidebar afterward.
  const act = (fn: () => void) => () => {
    fn();
    onClose();
  };

  return (
    <div className={`sidebar-root ${open ? "open" : ""}`} aria-hidden={!open}>
      <div className="sidebar-scrim" onClick={onClose} />
      <aside className="sidebar-panel" role="dialog" aria-label={t("nav.menu")}>
        <div className="sidebar-head">
          <button type="button" className="brand-toggle" onClick={onClose} aria-label={t("nav.collapse")} title={t("nav.collapse")}>
            <span className="title-logo">-/</span>
          </button>
          <button type="button" className="icon-btn" onClick={onClose} aria-label={t("common.close")} title={t("common.close")}>
            <CloseIcon />
          </button>
        </div>

        <nav className="sidebar-actions">
          <button type="button" className="sidebar-action" onClick={act(onNewChat)}>
            <NewChatIcon />
            <span>{t("history.new")}</span>
          </button>
          <button type="button" className="sidebar-action" onClick={act(onOpenSettings)}>
            <SettingsIcon />
            <span>{t("settings.title")}</span>
          </button>
          {!isLocal && onOpenCron ? (
            <button type="button" className="sidebar-action" onClick={act(onOpenCron)}>
              <ScheduledIcon />
              <span>{t("cron.title")}</span>
            </button>
          ) : null}
          {!isLocal && onOpenBilling ? (
            <button type="button" className="sidebar-action" onClick={act(onOpenBilling)}>
              <BillingIcon />
              <span>{t("billing.open")}</span>
            </button>
          ) : null}
          {onOpenAdmin ? (
            <button type="button" className="sidebar-action" onClick={act(onOpenAdmin)}>
              <AdminIcon />
              <span>{t("admin.title")}</span>
            </button>
          ) : null}
          <button type="button" className="sidebar-action danger" onClick={act(onSignOut)}>
            <SignOutIcon />
            <span>{t("header.signOut")}</span>
          </button>
        </nav>

        <div className="sidebar-divider" />

        <div className="sidebar-section-title">{isLocal ? t("nav.tasks") : t("history.title")}</div>
        <div className="history-list">{children}</div>

        {email ? <div className="sidebar-foot">{email}</div> : null}
      </aside>
    </div>
  );
}
